import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/contexts/OrganizationContext";
import type {
  Contact,
  ContactImportRecord,
  ContactInput,
  ContactScore,
  ContactSocialProfile,
  ContactSource,
  ContactStatus,
} from "./types";
import { CONTACT_STATUSES } from "./types";
import type { LeadType } from "@/lib/lead-type";
import { makeContracts } from "@/features/crm-fields/contracts";
import { makeSupabaseBackend } from "@/features/crm-fields/supabase-backend";
import { normalizeCompanyName } from "@/lib/companies/match";
import { softDeleteCompaniesForOrg } from "@/features/companies/soft-delete";
import { normalizeLeadSourceInput } from "@/lib/lead-sources";
import { contactMutationErrorMessage } from "./lib/contact-mutation-error";
import {
  DEFAULT_CONTACT_LIST_SORT,
  getContactListOrderClauses,
  type ContactListSort,
} from "./contacts-list-sort";
import {
  buildMatchIndex,
  computeFillOnlyPatch,
  matchRow,
  normalizeEmailForMatch,
  normalizePhoneForMatch,
  registerContact,
  rowHasIdentifier,
  type DuplicateMode,
  type MatchableContact,
} from "./lib/import-matching";
import { dismissedPairKey } from "./lib/duplicate-detection";
import { coerceImportCustomFieldValue } from "./lib/coerce-custom-field-value";
import type { FieldType } from "@/lib/crm-fields/validate";

// The importer writes a wide, dynamic payload (lead_score, do_not_email,
// company_id, owner_id, …) that the hand-maintained generated types don't all
// model. To avoid generated-types churn we route the importer's INSERT/SELECT
// through this untyped helper (same pattern as documents/companies). The typed
// CRUD hooks above (useContactsList etc.) are unaffected and stay as-is.
function relayFrom(table: string) {
  return (supabase as unknown as SupabaseClient).schema("relay").from(table);
}

// Shape returned by the contacts→social_profiles join. The relay schema's
// FK is on contact_social_profiles → contacts (one-to-many), so supabase-js
// types the embedded resource as an array. We accept the polymorphic shape
// (single OR array OR null) defensively in case a future schema change
// flips the cardinality declaration.
type ContactRowWithSocialJoin = Omit<Contact, "social_profiles"> & {
  social_profiles?: ContactSocialProfile | ContactSocialProfile[] | null;
};

// ---------------------------------------------------------------------------
// Field selectors
// ---------------------------------------------------------------------------

const CONTACT_FIELDS = `
  id, organization_id, first_name, last_name, email, phone, mobile,
  company, company_id, job_title, website, avatar_url, status, lead_type, source, source_detail,
  follower_count,
  owner_id, do_not_email, ai_summary, ai_summary_updated_at,
  address_line1, address_line2, address_city, address_state,
  address_postal_code, address_country, notes, metadata,
  created_at, updated_at, deleted_at
`;

export const CONTACT_FIELDS_WITH_PROFILES = `
  ${CONTACT_FIELDS},
  social_profiles:contact_social_profiles ( id, platform, username, profile_url, verified )
`;

export function normalizeContact(raw: ContactRowWithSocialJoin): Contact {
  // Supabase relational selects return joined rows as array OR object
  // depending on the cardinality declared by the FK. Normalize to always-array
  // so downstream React doesn't have to deal with the polymorphism.
  const profiles = Array.isArray(raw.social_profiles)
    ? raw.social_profiles
    : raw.social_profiles
      ? [raw.social_profiles]
      : [];
  return { ...raw, social_profiles: profiles };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

interface ContactsListFilter {
  status?: ContactStatus | "all";
  leadType?: LeadType | "all";
  // Richer server-side filters — each is conditional; omit (undefined) to skip.
  ownerId?: string;
  companyId?: string;
  // Display name of the company being filtered. When provided alongside
  // companyId, also matches contacts that carry the free-text `company` label
  // for that account without a company_id link (see the company filter below).
  companyName?: string;
  source?: string;
  hasEmail?: boolean;
  hasPhone?: boolean;
  doNotEmail?: boolean;
  // Sort — defaults to created_at / desc, preserving the historical behavior.
  sort?: ContactListSort;
  // Fetch every server-filtered page before client-only filtering/sorting so
  // local work never runs on a capped 500-row window.
  fetchAllForClientFiltering?: boolean;
}

const CONTACTS_LIST_PAGE_SIZE = 500;
const CONTACT_SCORE_PAGE_SIZE = 1000;

export function useContactsList(filter: ContactsListFilter = {}) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["contacts", "list", organizationId, filter],
    queryFn: async (): Promise<Contact[]> => {
      if (!organizationId) return [];

      const buildQuery = () => {
        // Note: relay schema is the default on the Supabase client (see
        // src/lib/supabase.ts), so .from("contacts") resolves to relay.contacts.
        // Keep `query` a FilterBuilder across all conditional .eq/.not/.or calls;
        // apply .order() once at the tail (it returns a Transform builder that
        // no longer exposes the filter methods).
        let query = supabase
          .from("contacts")
          .select(CONTACT_FIELDS)
          .eq("organization_id", organizationId)
          .is("deleted_at", null);

        if (filter.status && filter.status !== "all") {
          query = query.eq("status", filter.status);
        }
        if (filter.leadType && filter.leadType !== "all") {
          query = query.eq("lead_type", filter.leadType);
        }
        if (filter.ownerId) {
          query = query.eq("owner_id", filter.ownerId);
        }
        if (filter.companyId) {
          // Match linked contacts (company_id) OR contacts that only carry the
          // free-text `company` label for the same account — the contact form
          // lets a user type a company name without linking an account row, so a
          // strict company_id match would silently drop those. Escape the name
          // for the PostgREST .or() grammar (same approach as useContactSearch).
          const companyName = filter.companyName?.trim();
          const escaped = companyName
            ?.replace(/[%_]/g, "")
            .replace(/\\/g, "\\\\")
            .replace(/"/g, '\\"');
          if (escaped) {
            query = query.or(
              `company_id.eq.${filter.companyId},company.ilike."${escaped}"`,
            );
          } else {
            query = query.eq("company_id", filter.companyId);
          }
        }
        if (filter.source) {
          query = query.eq("source", filter.source);
        }
        if (filter.hasEmail) {
          query = query.not("email", "is", null);
        }
        if (filter.hasPhone) {
          // "Has phone" = a landline OR a mobile — relay.contacts splits the two.
          query = query.or("phone.not.is.null,mobile.not.is.null");
        }
        if (filter.doNotEmail) {
          query = query.eq("do_not_email", true);
        }

        for (const order of getContactListOrderClauses(
          filter.sort ?? DEFAULT_CONTACT_LIST_SORT,
        )) {
          query = query.order(order.column, {
            ascending: order.ascending,
            nullsFirst: order.nullsFirst,
          });
        }

        return query;
      };

      if (filter.fetchAllForClientFiltering) {
        const rows: Contact[] = [];

        for (let from = 0; ; from += CONTACTS_LIST_PAGE_SIZE) {
          const to = from + CONTACTS_LIST_PAGE_SIZE - 1;
          const { data, error } = await buildQuery().range(from, to);
          if (error) throw error;

          const page = (data ?? []) as Contact[];
          rows.push(...page);

          if (page.length < CONTACTS_LIST_PAGE_SIZE) return rows;
        }
      }

      const { data, error } = await buildQuery().limit(CONTACTS_LIST_PAGE_SIZE);
      if (error) throw error;
      // Cast: several DB columns are text-typed without enum / CHECK
      // constraints (`status`, `source`) and return as `string | null`,
      // but our app invariants narrow them to the strict ContactStatus
      // and ContactSource unions. The cast surfaces these assumptions
      // explicitly. If the DB ever holds an out-of-union value, it will
      // render unexpectedly in the UI but won't crash. (Future migration:
      // promote these to Postgres enums to enforce at the boundary.)
      return (data ?? []) as Contact[];
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

export function useContact(contactId: string | undefined) {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["contacts", "detail", organizationId, contactId],
    queryFn: async (): Promise<Contact | null> => {
      if (!contactId || !organizationId) return null;

      const { data, error } = await supabase
        .from("contacts")
        .select(CONTACT_FIELDS_WITH_PROFILES)
        .eq("id", contactId)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .maybeSingle();

      if (error) throw error;
      // Cast at the boundary: same status/source widening pattern as
      // useContactsList — the DB columns are text-typed, our app
      // invariant narrows to the strict ContactStatus / ContactSource
      // unions. normalizeContact's parameter type asserts the narrow
      // shape; this cast acknowledges the widening explicitly.
      return data ? normalizeContact(data as ContactRowWithSocialJoin) : null;
    },
    enabled: !!contactId && !!organizationId,
    staleTime: 30 * 1000,
  });
}

export function useContactImports() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["contact-imports", organizationId],
    queryFn: async (): Promise<ContactImportRecord[]> => {
      if (!organizationId) return [];
      const { data, error } = await supabase
        .from("contact_imports")
        .select("*")
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      // Cast: same shape as useContacts — DB `status` is text-typed but our
      // app invariant narrows to the ContactImportStatus union.
      return (data ?? []) as ContactImportRecord[];
    },
    enabled: !!organizationId,
    staleTime: 10 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateContact() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (input: ContactInput): Promise<Contact> => {
      if (!organizationId) throw new Error("No active organization");
      const { data, error } = await supabase
        .from("contacts")
        .insert({ ...input, organization_id: organizationId })
        .select(CONTACT_FIELDS)
        .single();
      if (error) throw error;
      return data as Contact;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useUpdateContact(contactId: string) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (input: ContactInput): Promise<Contact> => {
      if (!organizationId) throw new Error("No active organization");
      const { data, error } = await supabase
        .from("contacts")
        .update(input)
        .eq("id", contactId)
        .eq("organization_id", organizationId)
        .select(CONTACT_FIELDS)
        .single();
      if (error) throw error;
      return data as Contact;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["contacts", "list"] });
      queryClient.setQueryData<Contact | null>(
        ["contacts", "detail", organizationId, contactId],
        (prev) => (prev ? { ...prev, ...data } : null)
      );
    },
  });
}

/** The shared DB command authorizes and deletes the complete batch atomically.
 * Never fall back to a direct deleted_at PATCH if the command is unavailable. */
async function softDeleteContactsForOrg(organizationId: string | null, ids: string[]): Promise<number> {
  if (ids.length === 0) return 0;
  if (!organizationId) throw new Error("No active organization");
  if (ids.length > 200) throw new Error("Select at most 200 contacts to delete together.");
  const { data, error } = await supabase.rpc("soft_delete_contacts", {
    p_org_id: organizationId,
    p_ids: ids,
  });
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") {
      throw new Error("Contact deletion is temporarily unavailable. Please try again later.");
    }
    throw error;
  }
  if (typeof data !== "number" || !Number.isSafeInteger(data) || data < 0 || data > new Set(ids).size) {
    throw new Error("Contact deletion could not be confirmed. Refresh before trying again.");
  }
  return data;
}

/** Soft-delete through the authenticated, org-scoped database command. */
export function useSoftDeleteContact() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: (contactId: string) => softDeleteContactsForOrg(organizationId, [contactId]),
    retry: false,
    onSuccess: (_data, contactId) => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.removeQueries({
        queryKey: ["contacts", "detail", organizationId, contactId],
      });
    },
  });
}

/**
 * Fetch recent contacts ordered by updated_at DESC. Used as the default
 * empty-state list in ContactPicker — sales reps usually want the people
 * they just spoke with.
 */
export function useRecentContacts(
  limit = 10,
  opts: { organizationId?: string | null } = {},
) {
  const { organizationId: contextOrganizationId } = useOrganization();
  const organizationId = opts.organizationId ?? contextOrganizationId;
  return useQuery({
    queryKey: ["contacts", "recent", organizationId, limit],
    enabled: !!organizationId,
    staleTime: 30_000,
    queryFn: async (): Promise<Contact[]> => {
      if (!organizationId) return [];
      const { data, error } = await supabase
        .from("contacts")
        .select(CONTACT_FIELDS)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .order("updated_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Contact[];
    },
  });
}

/**
 * Search contacts by name/email/company. Debounce-friendly — the hook
 * returns the same query key when the trimmed query is identical, so
 * rapid keystrokes don't trigger extra fetches. Empty/whitespace query
 * returns an empty result (caller should fall back to useRecentContacts).
 *
 * Pass `{ searchPhone: true }` to also match `phone`/`mobile`. It's opt-in
 * because phone numbers are stored free-form ('555-123-4567', '5551234567'),
 * so an ilike substring match only catches numbers typed in the same format
 * they're stored — useful as a secondary lookup (e.g. the SMS manual-list
 * picker) but noisy for the default name/email contact pickers.
 */
export function useContactSearch(
  query: string,
  limit = 20,
  opts: { searchPhone?: boolean; organizationId?: string | null } = {},
) {
  const { organizationId: contextOrganizationId } = useOrganization();
  const organizationId = opts.organizationId ?? contextOrganizationId;
  const trimmed = query.trim();
  const searchPhone = opts.searchPhone ?? false;
  return useQuery({
    queryKey: ["contacts", "search", organizationId, trimmed, limit, searchPhone],
    enabled: !!organizationId && trimmed.length >= 1,
    staleTime: 15_000,
    queryFn: async (): Promise<Contact[]> => {
      if (!organizationId || trimmed.length === 0) return [];
      // Match against first_name, last_name, email, company (case-insensitive).
      // Postgres `ilike` is fine for this — the contacts table is small per-tenant.
      const escapedSearch = trimmed
        .replace(/[%_]/g, "")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"');
      const like = `"%${escapedSearch}%"`;
      const orParts = [
        `first_name.ilike.${like}`,
        `last_name.ilike.${like}`,
        `email.ilike.${like}`,
        `company.ilike.${like}`,
      ];
      if (searchPhone) {
        orParts.push(`phone.ilike.${like}`, `mobile.ilike.${like}`);
      }
      const { data, error } = await supabase
        .from("contacts")
        .select(CONTACT_FIELDS)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .or(orParts.join(","))
        .order("updated_at", { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as Contact[];
    },
  });
}

/**
 * Fetch a specific set of contacts by id (org-scoped, excludes soft-deleted).
 * Used to hydrate already-selected chips in a multi-select picker where the
 * audience filter persists only contact_ids — so a saved campaign reopens with
 * its picked contacts shown by name/phone, not bare ids. Returns contacts in no
 * guaranteed order; the caller maps them by id.
 */
export function useContactsByIds(
  ids: string[],
  opts: { organizationId?: string | null } = {},
) {
  const { organizationId: contextOrganizationId } = useOrganization();
  const organizationId = opts.organizationId ?? contextOrganizationId;
  // Dedupe + sort so reorderings/dupes in `ids` don't thrash the cache key.
  const uniqueIds = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ["contacts", "by-ids", organizationId, uniqueIds],
    enabled: !!organizationId && uniqueIds.length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<Contact[]> => {
      if (!organizationId || uniqueIds.length === 0) return [];
      const { data, error } = await supabase
        .from("contacts")
        .select(CONTACT_FIELDS)
        .eq("organization_id", organizationId)
        .is("deleted_at", null)
        .in("id", uniqueIds);
      if (error) throw error;
      return (data ?? []) as Contact[];
    },
  });
}

// ---------------------------------------------------------------------------
// Import flow
// ---------------------------------------------------------------------------

export interface ParsedContactRow {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  company?: string | null; // free-text label AND link-by-name source
  job_title?: string | null;
  website?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_postal_code?: string | null;
  address_country?: string | null;
  lead_score?: string | null;
  do_not_email?: string | null;
  status?: string | null;
  source?: string | null;
  owner_id?: string | null;
  notes?: string | null;
  customFields?: Record<string, string>; // field_key -> raw value
  tags?: string[]; // slugs
}

export interface ImportSummary {
  importId: string;
  imported: number;
  /** Existing contacts enriched in update-fill-blanks mode. */
  updated: number;
  skipped: number;
  errors: number;
  errorDetails: Record<string, string>;
}

/**
 * All contacts of the org projected down to the fields duplicate matching
 * needs. Paginated because PostgREST caps a response at ~1000 rows — a
 * single un-ranged select silently truncates for larger orgs (which is how
 * the old email-only dedupe hoist could miss duplicates).
 */
export async function fetchContactMatchRows(
  organizationId: string,
): Promise<MatchableContact[]> {
  const PAGE = 1000;
  const out: MatchableContact[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await relayFrom("contacts")
      .select("id, email, phone, mobile, deleted_at")
      .eq("organization_id", organizationId)
      .order("id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const page = (data ?? []) as MatchableContact[];
    out.push(...page);
    if (page.length < PAGE) return out;
  }
}

/**
 * Match rows for the import PREVIEW — lets the UI show which CSV rows hit
 * existing contacts (and how) before anything is written.
 */
export function useContactMatchRows(enabled: boolean) {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ["contacts", "match-rows", organizationId],
    queryFn: () => fetchContactMatchRows(organizationId!),
    enabled: enabled && !!organizationId,
    staleTime: 30 * 1000,
  });
}

function coerceContactStatus(v: string | null | undefined): ContactStatus {
  const s = (v ?? "").trim().toLowerCase();
  return (CONTACT_STATUSES as readonly string[]).includes(s)
    ? (s as ContactStatus)
    : "new";
}

function coerceContactSource(v: string | null | undefined): ContactSource {
  // source is free text (built-in token or custom). Canonicalize known
  // built-ins from CSV/user casing (e.g. "Referral", "Website") while
  // preserving unknown custom values as entered. Empty falls back to "import"
  // (this path is the CSV-import row mapping).
  return normalizeLeadSourceInput(v, "import");
}

const TRUTHY = new Set(["true", "1", "yes", "y"]);
function coerceBool(v: string | null | undefined): boolean {
  return TRUTHY.has((v ?? "").trim().toLowerCase());
}

/**
 * Mutation that drives a CSV import end-to-end:
 *  1. Insert a relay.contact_imports row with status='processing'.
 *  2. Hoist identifier + company-name match maps (paginated).
 *  3. Per-row loop: match existing contacts by email, then phone/mobile.
 *     Matches follow `duplicateMode`: "skip" leaves them untouched (the
 *     historical behavior); "update" enriches the existing contact
 *     fill-blanks-only (never overwrites, never nulls). Unmatched rows
 *     insert as before: link-or-create a company by name, insert the
 *     contact, apply custom fields + tags. Errors are isolated per row —
 *     one bad row never aborts the batch.
 *  4. Update the import row with final counters + status. The migration's
 *     RLS policy permits this UPDATE because we're still the importer
 *     (imported_by = auth.uid()) and the row hasn't yet completed.
 *
 * Returns a summary so the UI can show "created / updated / skipped / errors".
 */
export function useImportContacts() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async ({
      fileName,
      rows,
      duplicateMode = "skip",
    }: {
      fileName: string;
      rows: ParsedContactRow[];
      duplicateMode?: DuplicateMode;
    }): Promise<ImportSummary> => {
      if (!organizationId) throw new Error("No active organization");

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id ?? null;

      // 1. Create the import row
      const { data: importRow, error: importErr } = await supabase
        .from("contact_imports")
        .insert({
          organization_id: organizationId,
          imported_by: userId,
          file_name: fileName,
          total_rows: rows.length,
          status: "processing",
        })
        .select()
        .single();
      if (importErr) throw importErr;

      const contracts = makeContracts(makeSupabaseBackend(organizationId));

      // Custom-field defs, org-scoped: CSV cells are always plain strings,
      // but setValue's validateFieldValue requires a real boolean for
      // "boolean" and a string[] for "multiselect" — coerceImportCustomFieldValue
      // needs each column's field_type/options to convert a raw cell into
      // the shape the def actually expects (see #5410 Codex round 2).
      //
      // Paginated — same reason as fetchContactMatchRows/companyByName below:
      // a plain select silently truncates at the PostgREST row cap. An
      // organization with more definitions than the cap would otherwise omit
      // some from fieldDefsByKey, so an affected boolean/date/multiselect
      // column falls through to coerceImportCustomFieldValue's raw-string
      // path, fails setValue's validation, and reports a row error AFTER the
      // base contact has already been inserted and counted as imported
      // (Codex P2, PR #5515 round 21).
      const fieldDefRows: Array<{ field_key: string; field_type: FieldType; options: string[] | null }> = [];
      {
        const PAGE = 1000;
        for (let from = 0; ; from += PAGE) {
          const { data, error } = await relayFrom("custom_field_defs")
            .select("field_key, field_type, options")
            .eq("organization_id", organizationId)
            .eq("entity_type", "contact")
            .order("field_key", { ascending: true })
            .range(from, from + PAGE - 1);
          if (error) throw error;
          const page = (data ?? []) as Array<{ field_key: string; field_type: FieldType; options: string[] | null }>;
          fieldDefRows.push(...page);
          if (page.length < PAGE) break;
        }
      }
      const fieldDefsByKey = new Map(fieldDefRows.map((d) => [d.field_key, d]));

      let imported = 0;
      let updated = 0;
      let skipped = 0;
      let errors = 0;
      const errorDetails: Record<string, string> = {};

      // 2a. Hoist all existing identifiers (paginated — a plain select
      // silently truncates at the PostgREST row cap) and index them for
      // email→phone duplicate matching. Soft-deleted rows stay
      // email-matchable because UNIQUE (organization_id, email) spans them.
      const matchIndex = buildMatchIndex(
        await fetchContactMatchRows(organizationId),
      );

      // 2b. Hoist company-name match map for link-by-name. Paginated for
      // the same reason as fetchContactMatchRows — a single select truncates
      // at the PostgREST row cap and would create duplicate companies.
      const companyByName = new Map<string, string>();
      {
        const PAGE = 1000;
        for (let from = 0; ; from += PAGE) {
          const { data, error } = await relayFrom("companies")
            .select("id, name")
            .eq("organization_id", organizationId)
            .is("deleted_at", null)
            .order("id", { ascending: true })
            .range(from, from + PAGE - 1);
          if (error) throw error;
          const page = (data ?? []) as Array<{ id: string; name: string }>;
          for (const c of page) {
            companyByName.set(normalizeCompanyName(c.name), c.id);
          }
          if (page.length < PAGE) break;
        }
      }

      // 2c. Update mode: pre-fetch full rows for matched ACTIVE contacts so
      // fill-only patches can compare against current values.
      const existingById = new Map<string, Record<string, unknown>>();
      if (duplicateMode === "update") {
        const matchedIds = new Set<string>();
        for (const row of rows) {
          const m = matchRow(row, matchIndex);
          if (m.kind === "match" && !m.contact.deleted_at) matchedIds.add(m.contact.id);
        }
        const ids = [...matchedIds];
        for (let i = 0; i < ids.length; i += 200) {
          const { data, error } = await relayFrom("contacts")
            .select(
              "id, first_name, last_name, email, phone, mobile, company, company_id, lead_type, job_title, website, address_line1, address_line2, address_city, address_state, address_postal_code, address_country, lead_score, do_not_email, status, source, owner_id, notes",
            )
            .in("id", ids.slice(i, i + 200));
          if (error) throw error;
          for (const r of (data ?? []) as Array<Record<string, unknown> & { id: string }>) {
            existingById.set(r.id, r);
          }
        }
      }

      // Company link-by-name, match-or-create — shared by the create path
      // and by update-mode enrichment. `created` lets the caller clean up
      // an orphan company when the contact write it was resolved for gets
      // skipped (a "skipped" row must be side-effect-free).
      async function resolveCompanyId(
        companyLabel: string,
      ): Promise<{ id: string; created: boolean }> {
        const norm = normalizeCompanyName(companyLabel);
        const matched = companyByName.get(norm);
        if (matched) return { id: matched, created: false };
        const { data: created, error: coErr } = await relayFrom("companies")
          .insert({
            organization_id: organizationId,
            name: companyLabel,
            status: "new",
            source: "import",
          })
          .select("id")
          .single();
        if (coErr) throw coErr;
        const id = (created as { id: string }).id;
        companyByName.set(norm, id);
        return { id, created: true };
      }

      // Update-fill-blanks writer. Returns true when anything was written.
      async function enrichExistingContact(
        contactId: string,
        row: ParsedContactRow,
      ): Promise<boolean> {
        const existing = existingById.get(contactId)!;

        // Only values actually present in the CSV participate — the create
        // path's defaults (status "new", source "import", do_not_email
        // false) must not masquerade as incoming data here.
        const provided: Record<string, unknown> = {};
        const scalars: Array<[string, string | null | undefined]> = [
          ["first_name", row.first_name],
          ["last_name", row.last_name],
          ["email", row.email?.trim().toLowerCase()],
          ["phone", row.phone],
          ["mobile", row.mobile],
          ["job_title", row.job_title],
          ["website", row.website],
          ["address_line1", row.address_line1],
          ["address_line2", row.address_line2],
          ["address_city", row.address_city],
          ["address_state", row.address_state],
          ["address_postal_code", row.address_postal_code],
          ["address_country", row.address_country],
          ["owner_id", row.owner_id],
          ["notes", row.notes],
        ];
        for (const [key, value] of scalars) {
          const t = value?.trim();
          if (t) provided[key] = t;
        }
        const leadScore = row.lead_score?.trim();
        if (leadScore && Number.isFinite(Number(leadScore))) {
          provided.lead_score = Number(leadScore);
        }
        if (row.status?.trim()) provided.status = coerceContactStatus(row.status);
        if (row.source?.trim()) provided.source = coerceContactSource(row.source);

        const patch = computeFillOnlyPatch(existing, provided);

        // Never fill an identifier that belongs to a DIFFERENT contact —
        // e.g. a row that email-matched contact A but carries contact B's
        // phone. Filling it would either trip the unique phone index or
        // silently cross-wire contact records.
        const emailFill = patch.email as string | undefined;
        if (emailFill) {
          const normalized = normalizeEmailForMatch(emailFill)!;
          if (matchIndex.byEmail.has(normalized)) {
            const owner = matchIndex.byEmail.get(normalized);
            // null = email case-folds onto multiple contacts (ambiguous).
            if (owner === null || owner!.id !== contactId) delete patch.email;
          }
        }
        for (const key of ["phone", "mobile"] as const) {
          const fill = patch[key] as string | undefined;
          if (!fill) continue;
          const normalized = normalizePhoneForMatch(fill);
          if (!normalized || !matchIndex.byPhone.has(normalized)) continue;
          const owner = matchIndex.byPhone.get(normalized);
          // null = digits shared by multiple contacts (ambiguous) — never
          // fill those either.
          if (owner === null || owner!.id !== contactId) delete patch[key];
        }

        // do_not_email is a one-way ratchet: an incoming truthy value may
        // flip false → true (suppression is compliance-relevant), but an
        // import never un-suppresses.
        if (coerceBool(row.do_not_email) && existing.do_not_email !== true) {
          patch.do_not_email = true;
        }

        // Company: link only when the contact has no account yet AND its
        // free-text label (if any) agrees with the CSV — otherwise we'd
        // attach a conflicting account. lead_type is untouched on update.
        const companyLabel = row.company?.trim() || null;
        const existingLabel =
          typeof existing.company === "string" ? existing.company.trim() : "";
        if (
          companyLabel &&
          !existing.company_id &&
          (!existingLabel ||
            normalizeCompanyName(existingLabel) === normalizeCompanyName(companyLabel))
        ) {
          patch.company_id = (await resolveCompanyId(companyLabel)).id;
          if (!existingLabel) patch.company = companyLabel;
          // Mirror the create path's classification: linking a first
          // company account upgrades the default b2c to b2b. Any other
          // lead_type was chosen deliberately and stays.
          if (existing.lead_type === "b2c") patch.lead_type = "b2b";
        }

        let wrote = false;
        if (Object.keys(patch).length > 0) {
          let { error } = await relayFrom("contacts")
            .update(patch)
            .eq("id", contactId);
          // A filled email can still collide with a SOFT-DELETED contact's
          // email (RLS hides those rows, so the collision-guard above can't
          // see them, and UNIQUE (org, email) spans deleted rows). Drop the
          // email fill and keep the rest of the enrichment.
          const pgErr = error as { code?: string; message?: string } | null;
          if (
            pgErr?.code === "23505" &&
            pgErr.message?.includes("contacts_email_unique") &&
            "email" in patch
          ) {
            delete patch.email;
            if (Object.keys(patch).length > 0) {
              ({ error } = await relayFrom("contacts")
                .update(patch)
                .eq("id", contactId));
            } else {
              error = null;
            }
          }
          if (error) throw error;
          // Merge the applied patch back into the snapshot: a later row
          // matching the same contact must see current values, or it would
          // "fill" fields this row just wrote (last-row-wins overwrite) and
          // double-count `updated`.
          Object.assign(existing, patch);
          // Register the merged identifiers IMMEDIATELY after the scalar
          // write commits — if a later custom-field/tag write throws, the
          // row errors but the identifiers are already in the DB, and later
          // rows must still match them instead of tripping the constraints.
          registerContact(matchIndex, {
            id: contactId,
            email: (existing.email as string | null) ?? null,
            phone: (existing.phone as string | null) ?? null,
            mobile: (existing.mobile as string | null) ?? null,
            deleted_at: null,
          });
          wrote = Object.keys(patch).length > 0;
        }

        // Custom fields fill blanks only; tags are additive (applyTag is
        // idempotent, so re-applying an existing tag is a no-op).
        for (const [field_key, raw] of Object.entries(row.customFields ?? {})) {
          if (raw == null || raw === "") continue;
          const current = await contracts.getValue("contact", contactId, field_key);
          if (current == null || current === "") {
            const def = fieldDefsByKey.get(field_key);
            const value = def
              ? coerceImportCustomFieldValue(def.field_type, raw, def.options)
              : raw;
            await contracts.setValue("contact", contactId, field_key, value);
            wrote = true;
          }
        }
        for (const slug of row.tags ?? []) {
          if (!slug) continue;
          await contracts.ensureTag(slug, { name: slug });
          // Only count taggings that were actually added — otherwise an
          // idempotent re-import of the same file reports every tagged row
          // as "updated".
          if (await contracts.applyTag(slug, "contact", contactId)) {
            wrote = true;
          }
        }
        return wrote;
      }

      // 3. Per-row loop — isolate errors so one bad row never aborts the batch.
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const email = row.email?.trim().toLowerCase() || null;

        const match = matchRow(row, matchIndex);

        // Skip rows lacking any identifier (no email and no name) — unless
        // they match an existing contact: a phone-only row can still enrich
        // its match in update mode, and can never insert a nameless contact.
        if (match.kind === "none" && !rowHasIdentifier(row)) {
          skipped++;
          continue;
        }

        // Ambiguous rows (identifier shared by MULTIPLE existing contacts)
        // always skip: enriching would guess the wrong record, and inserting
        // would create yet another duplicate the raw-value unique indexes
        // can't catch across formatting differences.
        if (match.kind === "ambiguous") {
          skipped++;
          continue;
        }

        try {
          if (match.kind === "match") {
            // Soft-deleted matches always skip: the email unique constraint
            // forbids re-creating them, and silently resurrecting deleted
            // data via update would surprise. Same-batch matches (contacts
            // created earlier in THIS run, absent from existingById) also
            // skip — a repeated row adds nothing.
            if (
              duplicateMode !== "update" ||
              match.contact.deleted_at ||
              !existingById.has(match.contact.id)
            ) {
              skipped++;
              continue;
            }
            // Identifier registration happens INSIDE enrichExistingContact,
            // right after the scalar write — so it still runs when a later
            // custom-field/tag write throws the row into the error bucket.
            if (await enrichExistingContact(match.contact.id, row)) {
              updated++;
            } else {
              skipped++;
            }
            continue;
          }

          // Company link-by-name: match-or-create, set company_id; keep the
          // free-text label too.
          const companyLabel = row.company?.trim() || null;
          const companyRes = companyLabel ? await resolveCompanyId(companyLabel) : null;
          const companyId = companyRes?.id ?? null;

          const contactInsert = {
            organization_id: organizationId,
            first_name: row.first_name?.trim() || null,
            last_name: row.last_name?.trim() || null,
            email,
            phone: row.phone?.trim() || null,
            mobile: row.mobile?.trim() || null,
            company: companyLabel,
            company_id: companyId,
            // A contact linked to a company account is B2B; otherwise B2C.
            lead_type: companyId ? "b2b" : "b2c",
            job_title: row.job_title?.trim() || null,
            website: row.website?.trim() || null,
            address_line1: row.address_line1?.trim() || null,
            address_line2: row.address_line2?.trim() || null,
            address_city: row.address_city?.trim() || null,
            address_state: row.address_state?.trim() || null,
            address_postal_code: row.address_postal_code?.trim() || null,
            address_country: row.address_country?.trim() || null,
            lead_score:
              row.lead_score == null || row.lead_score.trim() === ""
                ? null
                : Number.isFinite(Number(row.lead_score.trim()))
                  ? Number(row.lead_score.trim())
                  : null,
            do_not_email: coerceBool(row.do_not_email),
            status: coerceContactStatus(row.status),
            source: coerceContactSource(row.source),
            owner_id: row.owner_id?.trim() || null,
            notes: row.notes?.trim() || null,
          };

          let insertedPhone = contactInsert.phone;
          let { data: inserted, error: insErr } = await relayFrom("contacts")
            .insert(contactInsert)
            .select("id")
            .single();

          let pgErr = insErr as { code?: string; message?: string } | null;
          const inputPhoneWasNonIdentifying =
            contactInsert.phone != null && normalizePhoneForMatch(contactInsert.phone) === null;
          if (
            pgErr?.code === "23505" &&
            pgErr.message?.includes("contacts_phone_unique") &&
            contactInsert.phone &&
            inputPhoneWasNonIdentifying
          ) {
            // The raw phone index can reject placeholders/extensions that the
            // matcher deliberately treats as non-identifying. Keep the valid
            // contact row and drop only that bad phone value.
            ({ data: inserted, error: insErr } = await relayFrom("contacts")
              .insert({ ...contactInsert, phone: null })
              .select("id")
              .single());
            insertedPhone = null;
            pgErr = insErr as { code?: string; message?: string } | null;
          }

          if (insErr) {
            // Identifier unique violations that reach the insert are
            // duplicate-skips, not row errors:
            //  - email: UNIQUE (org, email) spans soft-deleted contacts,
            //    which RLS hides from the match fetch;
            //  - phone: after non-identifying raw phone values have already
            //    been retried without phone, any remaining collision is a
            //    matchable duplicate or a concurrent import racing this one.
            if (
              pgErr?.code === "23505" &&
              (pgErr.message?.includes("contacts_email_unique") ||
                pgErr.message?.includes("contacts_phone_unique"))
            ) {
              // A skipped duplicate must be side-effect-free: if the company
              // was created FOR this row, remove it again. relay.companies
              // has NO DELETE policy, and a direct deleted_at PATCH fails its
              // live-row SELECT policy (#163), so soft-delete through the
              // guarded command. A cleanup that fails leaves an orphan
              // company, so report it as a row error rather than hiding it.
              if (companyRes?.created && companyLabel) {
                // A concurrent import may have linked this company in the
                // window since we created it — never hide a company that
                // contacts already reference. On any doubt (count error),
                // leave the company alone: an empty account is recoverable,
                // a hidden linked one is not.
                const { count: refCount, error: refErr } = await relayFrom("contacts")
                  .select("id", { count: "exact", head: true })
                  .eq("company_id", companyRes.id);
                if (!refErr && (refCount ?? 0) === 0) {
                  try {
                    await softDeleteCompaniesForOrg(organizationId, [companyRes.id]);
                    companyByName.delete(normalizeCompanyName(companyLabel));
                  } catch (cleanupErr) {
                    // The command may have committed even though its result
                    // was lost or unusable. Keep reusing this company for
                    // later rows only if it is visibly still live.
                    const { data: stillLive, error: liveErr } = await relayFrom("companies")
                      .select("id")
                      .eq("id", companyRes.id)
                      .maybeSingle();
                    if (liveErr || !stillLive) {
                      companyByName.delete(normalizeCompanyName(companyLabel));
                    }
                    errors++;
                    errorDetails[`row_${i + 1}`] =
                      `Skipped as a duplicate, but the company "${companyLabel}" created for it could not be removed: ${
                        cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr)
                      }`;
                  }
                }
              }
              skipped++;
              continue;
            }
            throw insErr;
          }
          const contactId = (inserted as { id: string }).id;
          imported++;
          // Register the new contact so later rows in this batch match it
          // instead of tripping the unique constraints.
          registerContact(matchIndex, {
            id: contactId,
            email,
            phone: insertedPhone,
            mobile: row.mobile?.trim() || null,
            deleted_at: null,
          });

          for (const [field_key, raw] of Object.entries(row.customFields ?? {})) {
            if (raw == null || raw === "") continue;
            const def = fieldDefsByKey.get(field_key);
            const value = def
              ? coerceImportCustomFieldValue(def.field_type, raw, def.options)
              : raw;
            await contracts.setValue("contact", contactId, field_key, value);
          }
          for (const slug of row.tags ?? []) {
            if (!slug) continue;
            await contracts.ensureTag(slug, { name: slug });
            await contracts.applyTag(slug, "contact", contactId);
          }
        } catch (e) {
          errors++;
          errorDetails[`row_${i + 1}`] =
            e instanceof Error ? e.message : String(e);
        }
      }

      // 4. Finalize the import row. The table has no updated_count column,
      // so imported_count keeps meaning "created"; the updated tally lives
      // only in the returned summary.
      const finalStatus = errors > 0 ? "failed" : "completed";
      const { error: updErr } = await supabase
        .from("contact_imports")
        .update({
          status: finalStatus,
          imported_count: imported,
          skipped_count: skipped,
          error_count: errors,
          error_details: errorDetails,
        })
        .eq("id", importRow.id)
        .eq("organization_id", organizationId);

      if (updErr) {
        // Non-fatal — the contacts already imported are real, just the
        // import-row status is stuck on processing. Log so we know.
        console.warn("[import] failed to finalize import row:", updErr.message);
      }

      return {
        importId: importRow.id,
        imported,
        updated,
        skipped,
        errors,
        errorDetails,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contact-imports"] });
    },
  });
}

// ---------------------------------------------------------------------------
// AI contact summary
// ---------------------------------------------------------------------------
export interface GenerateContactSummaryInput {
  contact_id: string;
  organization_id: string;
}

export interface GenerateContactSummaryResponse {
  summary: string;
  tokens_used?: number;
}

/**
 * supabase-js's FunctionsHttpError only carries a generic "Edge Function
 * returned a non-2xx status code" message — the actual { code, error } body
 * relay-ai-contact-summary returns (e.g. ai_not_configured, ai_model,
 * ai_rate_limited) lives on error.context, which is the raw Response object.
 * Read it here so the UI can show the real cause instead of a generic
 * failure toast.
 */
async function describeEdgeFunctionError(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError && error.context instanceof Response) {
    try {
      const body = await error.context.clone().json();
      if (typeof body?.error === "string") return body.error;
    } catch {
      // Body wasn't JSON (or already consumed) — fall through to the generic message below.
    }
  }
  return error instanceof Error ? error.message : "Summary generation failed";
}

/**
 * Calls relay-ai-contact-summary, which generates a 2-3 sentence CRM summary
 * and persists it back to contacts.ai_summary + ai_summary_updated_at.
 * Invalidates ["contacts"] on success so ContactDetail re-renders with the
 * fresh ai_summary.
 */
export function useGenerateContactSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: GenerateContactSummaryInput,
    ): Promise<GenerateContactSummaryResponse> => {
      const { data, error } = await supabase.functions.invoke<GenerateContactSummaryResponse>(
        "relay-ai-contact-summary",
        { body: input },
      );
      if (error) throw new Error(await describeEdgeFunctionError(error));
      if (!data?.summary) throw new Error("relay-ai-contact-summary returned no summary");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Lead scoring hooks
// ---------------------------------------------------------------------------

/**
 * Fetches cached contact scores from relay.contact_scores for the org.
 * Scores are written by relay_persist_contact_scores() — call useScoreAllContacts
 * to recompute and persist them.
 */
export function useContactScores(orgId: string | undefined) {
  return useQuery({
    queryKey: ["contact_scores", orgId],
    enabled: !!orgId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ContactScore[]> => {
      if (!orgId) return [];

      const rows: ContactScore[] = [];
      for (let from = 0; ; from += CONTACT_SCORE_PAGE_SIZE) {
        const to = from + CONTACT_SCORE_PAGE_SIZE - 1;
        const { data, error } = await supabase
          .from("contact_scores" as never)
          .select("*")
          .eq("organization_id", orgId)
          .order("score", { ascending: false })
          .order("contact_id", { ascending: true })
          .range(from, to);

        if (error) throw error;

        const page = (data ?? []) as ContactScore[];
        rows.push(...page);

        if (page.length < CONTACT_SCORE_PAGE_SIZE) return rows;
      }
    },
  });
}

/**
 * Calls relay_persist_contact_scores(org_id), which re-scores all contacts
 * and upserts the results into relay.contact_scores. Returns the number of
 * contacts scored. Invalidates the contact_scores cache on success.
 */
export function useScoreAllContacts() {
  const qc = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (): Promise<number> => {
      if (!organizationId) throw new Error("org required");
      const { data, error } = await (supabase as unknown as SupabaseClient)
        .schema("public")
        .rpc("relay_persist_contact_scores", { p_org_id: organizationId });
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact_scores", organizationId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Bulk mutations
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Social profile smart-match (blueprint §4.2) — pulled from
// relay-social-suggest, which is pure heuristic over name/email/company.
// Strictly suggest-confirm: the EF never writes; the UI confirms each
// candidate before calling useUpsertContactSocialProfile.
// ---------------------------------------------------------------------------

export interface SocialSuggestion {
  platform: string;
  handle: string;
  profile_url: string;
  /** 0..1 — UI sorts descending. */
  confidence: number;
  reason: string;
  /** Provider tag — `heuristic` today, `enrichment` once a paid provider plugs in. */
  source: "heuristic" | "enrichment";
}

export function useSuggestContactSocialProfiles() {
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (contactId: string): Promise<SocialSuggestion[]> => {
      if (!organizationId) throw new Error("No active organization");
      const { data, error } = await supabase.functions.invoke<{
        suggestions: SocialSuggestion[];
      }>("relay-social-suggest", {
        body: { organization_id: organizationId, contact_id: contactId },
      });
      if (error) throw error;
      return data?.suggestions ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Social profiles — CRUD on relay.contact_social_profiles
// ---------------------------------------------------------------------------

/**
 * Input to upsert a contact_social_profiles row. Omit `id` to insert; include
 * it to update an existing row.
 */
function assertSafeHttpUrl(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    if (url.protocol === "http:" || url.protocol === "https:") return url.href;
  } catch {
    // Fall through to a consistent validation error below.
  }
  throw new Error("Profile URL must start with http:// or https://");
}

export interface UpsertContactSocialProfileInput {
  id?: string;
  platform: ContactSocialProfile["platform"];
  username: string | null;
  profile_url: string | null;
}

/**
 * Upsert a row in relay.contact_social_profiles for a contact. Both create
 * (no id) and update (id provided) go through the same call — the relay
 * schema's PK is just `id`, so supabase-js dispatches insert/update on
 * presence/absence of id. Invalidates the contact detail query so the header
 * icons + URL links re-render in place.
 */
export function useUpsertContactSocialProfile(contactId: string) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (input: UpsertContactSocialProfileInput): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");
      const row = {
        ...(input.id ? { id: input.id } : {}),
        organization_id: organizationId,
        contact_id: contactId,
        platform: input.platform,
        username: input.username,
        profile_url: assertSafeHttpUrl(input.profile_url),
      };
      const { error } = await (supabase as unknown as SupabaseClient)
        .schema("relay")
        .from("contact_social_profiles")
        .upsert(row);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contacts", "detail", organizationId, contactId],
      });
    },
  });
}

/**
 * Hard-delete a row from relay.contact_social_profiles. The table has no
 * soft-delete column — these rows are operator-curated metadata, not the
 * record of truth — so we delete outright and refresh the contact detail
 * query.
 */
export function useDeleteContactSocialProfile(contactId: string) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();

  return useMutation({
    mutationFn: async (profileId: string): Promise<void> => {
      const { error } = await (supabase as unknown as SupabaseClient)
        .schema("relay")
        .from("contact_social_profiles")
        .delete()
        .eq("id", profileId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contacts", "detail", organizationId, contactId],
      });
    },
  });
}

/** Patch a set of contacts in one Supabase UPDATE. */
export function useBulkUpdateContacts() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async ({
      ids,
      patch,
    }: {
      ids: string[];
      patch: Partial<ContactInput>;
    }): Promise<void> => {
      if (ids.length === 0) return;
      if (!organizationId) throw new Error("No active organization");
      const { error } = await supabase
        .from("contacts")
        .update(patch)
        .eq("organization_id", organizationId)
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not update contacts"),
  });
}

/**
 * Soft-delete up to 200 selected contacts in one atomic, authenticated RPC.
 * Same-org tombstones are successful retry no-ops. Never split a selection
 * into separate calls, which would lose all-or-none batch semantics.
 */
export function useBulkSoftDeleteContacts() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: (ids: string[]) => softDeleteContactsForOrg(organizationId, ids),
    retry: false,
    onSuccess: (_data, ids) => {
      // Match useSoftDeleteContact: invalidate all contacts caches (list,
      // search, recent, by-ids) and drop each deleted contact's detail
      // query so a stale cached detail view can't keep showing it.
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      for (const id of ids) {
        queryClient.removeQueries({
          queryKey: ["contacts", "detail", organizationId, id],
        });
      }
    },
    onError: (err) => toast.error(contactMutationErrorMessage(err, "Could not delete contacts")),
  });
}

// ---------------------------------------------------------------------------
// Duplicate contact merge / dismiss
// ---------------------------------------------------------------------------

/**
 * Org-scoped set of contact pairs the user has explicitly marked "not a
 * duplicate" (relay.contact_duplicate_resolutions, resolution='not_duplicate').
 * Nested under the ["contacts", ...] key prefix (matching this file's
 * ["contacts", "detail"|"list"|"search"|"recent"|"by-ids", organizationId, ...]
 * convention) so a merge's broad `invalidateQueries({ queryKey: ["contacts"] })`
 * also refreshes this query without a second explicit call.
 */
export function useDuplicateResolutions() {
  const { organizationId } = useOrganization();
  const query = useQuery({
    queryKey: ["contacts", "duplicate-resolutions", organizationId],
    enabled: !!organizationId,
    staleTime: 30_000,
    queryFn: async (): Promise<Set<string>> => {
      if (!organizationId) return new Set();
      // Page through the full set. An org with more dismissed pairs than the
      // PostgREST row cap would otherwise get a partial first page, and the
      // hook would treat that as the complete dismissed set — pairs beyond the
      // cap would resurface on the duplicates page and be mergeable/dismissable
      // again despite an existing resolution row.
      const keys = new Set<string>();
      for (let from = 0; ; from += CONTACTS_LIST_PAGE_SIZE) {
        const to = from + CONTACTS_LIST_PAGE_SIZE - 1;
        const { data, error } = await supabase
          .from("contact_duplicate_resolutions")
          .select("contact_a_id, contact_b_id")
          .eq("organization_id", organizationId)
          .eq("resolution", "not_duplicate")
          // Total order for stable offset paging — contact_a_id alone leaves
          // rows sharing an a-id in arbitrary order across page requests, so a
          // tied row on a page boundary could be skipped or double-counted.
          // (organization_id, contact_a_id, contact_b_id) is UNIQUE, so the
          // (a_id, b_id) pair is a total order within the org.
          .order("contact_a_id")
          .order("contact_b_id")
          .range(from, to);
        if (error) throw error;
        const page = data ?? [];
        for (const r of page) keys.add(dismissedPairKey(r.contact_a_id, r.contact_b_id));
        if (page.length < CONTACTS_LIST_PAGE_SIZE) break;
      }
      return keys;
    },
  });
  return {
    dismissedPairKeys: query.data ?? new Set<string>(),
    isLoading: query.isPending,
    // Surface load failures so the page can block instead of silently treating
    // an errored (empty) result as "nothing dismissed" — that would resurface
    // dismissed pairs as actionable on an RLS/PostgREST/migration failure.
    isError: query.isError,
    error: query.error as Error | null,
  };
}

/**
 * Calls relay.merge_contacts(survivor, loser, field_overrides): copies the
 * chosen field values onto the survivor, re-points the loser's child rows,
 * and soft-deletes the loser. onSuccess removes the loser's cached detail
 * query outright (it's a tombstone now) and invalidates the ["contacts"]
 * prefix to refresh the list + the duplicate-resolutions query in one call —
 * same shape as useBulkSoftDeleteContacts.
 */
export function useMergeContacts() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (args: {
      survivorId: string;
      loserId: string;
      fieldOverrides: Record<string, unknown>;
    }): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");
      const { error } = await supabase.rpc("merge_contacts", {
        p_organization_id: organizationId,
        p_survivor_id: args.survivorId,
        p_loser_id: args.loserId,
        // p_field_overrides is typed as Json server-side; the caller's
        // Record<string, unknown> patch isn't statically assignable to Json,
        // so this is the one intentional escape hatch (never `any`).
        p_field_overrides: args.fieldOverrides as never,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      // The loser is soft-deleted — drop its cached detail query outright (not
      // just invalidate) so navigating back to it can't briefly render the
      // tombstoned contact before the refetch resolves.
      queryClient.removeQueries({
        queryKey: ["contacts", "detail", organizationId, variables.loserId],
      });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      // The RPC deletes both contacts' relay.contact_scores rows; the score
      // cache lives under a separate key (useContactScores), so refetch it too
      // or the list's score sort/badges keep showing pre-merge scores.
      queryClient.invalidateQueries({ queryKey: ["contact_scores", organizationId] });
      toast.success("Contacts merged");
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not merge contacts"),
  });
}

/**
 * Calls relay.dismiss_contact_duplicate to record a pair as resolution='not_duplicate',
 * suppressing it from future duplicate-detection results without touching
 * either contact row. Only the resolutions query needs to refetch.
 *
 * No success toast here — the only caller (DuplicateContactsPage) needs the
 * toast to carry a pair-specific "Undo" action, so it shows its own toast in
 * the mutate-call's onSuccess. A hook-level toast here would double up.
 */
export function useDismissContactDuplicate() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (args: { contactAId: string; contactBId: string }): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");
      const { error } = await supabase.rpc("dismiss_contact_duplicate", {
        p_organization_id: organizationId,
        p_contact_a_id: args.contactAId,
        p_contact_b_id: args.contactBId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contacts", "duplicate-resolutions", organizationId],
      });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not dismiss"),
  });
}

/**
 * Reverses useDismissContactDuplicate's resolution, so the pair resurfaces in
 * duplicate-detection results. No success toast — this is an inline "undo"
 * action, not a standalone user-initiated operation.
 */
export function useUndismissContactDuplicate() {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganization();
  return useMutation({
    mutationFn: async (args: { contactAId: string; contactBId: string }): Promise<void> => {
      if (!organizationId) throw new Error("No active organization");
      const { error } = await supabase.rpc("undismiss_contact_duplicate", {
        p_organization_id: organizationId,
        p_contact_a_id: args.contactAId,
        p_contact_b_id: args.contactBId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contacts", "duplicate-resolutions", organizationId],
      });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not undo"),
  });
}
